import React, {
  CSSProperties,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { SemanticICONS } from "semantic-ui-react";

import moment from "moment";

// UX
import DropdownOptions from "react-lib/appcon/common/DropdownOptions";

type TimeComboBoxProps = {
  id?: string;
  inputStyle?: CSSProperties;
  style?: CSSProperties;
  // data
  defaultValue?: string;
  icon?: SemanticICONS;
  interval?: number;
  maxHour?: number;
  minHour?: number;
  placeholder?: string;
  // config
  disabled?: boolean;
  fluid?: boolean;
  noMinWidth?: boolean;
  // callback
  onFormatBeforeChange?: (value: string) => string;
  onTextChange?: (time: string) => any;
};

const IconStyle = { top: "0.7rem" };

const TimeComboBox = React.forwardRef<any, TimeComboBoxProps>((props, ref) => {
  const {
    id,
    defaultValue,
    disabled,
    fluid: isFluid,
    icon = "time",
    inputStyle,
    interval = 30,
    maxHour = 30,
    minHour = 0,
    noMinWidth,
    placeholder = "Choose Time",
    style,
    onFormatBeforeChange,
    onTextChange,
  } = props;

  const [timeList, setTimeList] = useState<any[]>([]);
  const [time, setTime] = useState("");

  const inputRef = useRef<any>(null);

  useImperativeHandle<any, any>(ref, () => ({
    getValue: () => time,
    inputRef,
    setValue: (value: string) => {
      setTime(value);
      generateTimeOptions(value);
    },
  }));

  // Effect
  useEffect(() => {
    generateTimeOptions();
  }, []);

  useEffect(() => {
    let value = defaultValue || "";

    const reg = /^\d{2}:\d{2}:\d{2}$/;

    if (value.search(reg) >= 0) {
      value = value.replaceAll(/:\d{2}$/g, "");
    }

    setTime(value);
    generateTimeOptions(value);
  }, [defaultValue]);

  const handleChangeTime = (value: string) => {
    // console.log("handleChangeTime", time);
    setTime(value);
    onTextChange?.(value);
    generateTimeOptions(value);
  };

  // Callback
  const handleChange = (e: any, v: any) => {
    if (v.value !== time) {
      handleChangeTime(v.value);
    }
  };

  // Memo
  const timeOptions = useMemo(
    () =>
      timeList.map((value, index) => ({
        key: index + 1,
        text: value,
        value,
      })),
    [timeList]
  );

  // Handler
  const generateTimeOptions = (inputTime = "") => {
    let [, min] = inputTime.split(":");

    min = min || ":00";

    const date = moment().format("MM/DD/YYYY");

    let generatedTimeList: string[] = [];

    const paddedMinHour = `0${minHour}`.slice(-2);

    let start = moment(`${date} ${paddedMinHour}${min}`, "MM/DD/YYYY HH:mm");

    const paddedMaxHour = `0${maxHour}`.slice(-2);

    let end = moment(`${date} ${paddedMaxHour}${min}`, "MM/DD/YYYY HH:mm");

    if (maxHour === 23) {
      end = moment(`${date} ${paddedMaxHour}:59`, "MM/DD/YYYY HH:mm");
    } else if (maxHour > 23) {
      const remainHours = maxHour - 23;
      const paddedRemainHours = `0${remainHours}`.slice(-2);

      end = moment(`${date} ${paddedRemainHours}${min}`, "MM/DD/YYYY HH:mm");
      end.add(1, "days");
    }

    while (start <= end) {
      const formattedTime = moment(start).format("HH:mm");

      generatedTimeList.push(formattedTime);
      start = moment(start).add(interval, "minutes");
    }

    if (inputTime) {
      const prev: string[] = [];
      const next: string[] = [];

      for (const value of generatedTimeList) {
        if (Intl.Collator().compare(inputTime, value) > 0) {
          next.push(value);
        } else {
          prev.push(value);
        }
      }

      generatedTimeList = [...prev, ...next];
    }

    setTimeList([...generatedTimeList] as any);
  };

  return (
    <DropdownOptions
      ref={inputRef}
      id={id}
      // config
      disabled={disabled}
      fluid={isFluid}
      icon={icon}
      iconStyle={IconStyle}
      inputStyle={inputStyle}
      noMinWidth={noMinWidth}
      placeholder={placeholder}
      style={style}
      value={time}
      formatTimeOnBlur
      search
      options={timeOptions}
      // callback
      onChange={handleChange}
      onFormatBeforeChange={onFormatBeforeChange}
    />
  );
});

TimeComboBox.displayName = "TimeComboBox";

export default React.memo(TimeComboBox);
